import { Typography } from '~/UIKit';
import React from 'react';
import { Calendar } from /* webpackPrefetch: true */ '@fitogram/react-calendar';
import '@fitogram/react-calendar/dist/index.css';
import CalendarDay from './CalendarDay/CalendarDay.component';
import { FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { BORDER_COLOR_SHADE_2, CHARCOAL_GREY, WHITE } from '~/Constants/Colors.constants';
import { CalendarNavigation } from './CalendarNavigation';

import { LanguagesEnum } from '~/Constants/Languages.constants';
import { makeStyles } from '~/UIKit/Style';

export interface CalendarComponentProps {
  calendarController: any;
  onTodayClick: () => void;
  dailyEventCount: Record<string, number>;
  currentLanguage: string;
}

const useStyles = makeStyles({
  calendarContainer: {
    borderBottom: `thin solid ${BORDER_COLOR_SHADE_2}`,
    paddingBottom: 15,
    background: WHITE,
  },
  calendarRoot: {
    width: '100% !important',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 200,
    backgroundColor: WHITE,
    paddingBottom: 5,
    borderRadius: '0 !important',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
    },
  },
  title: {
    fontSize: '34px',
    fontWeight: 'bold',
    gridArea: 'header',
  },
  calendarWeekday: {
    color: CHARCOAL_GREY,
    fontSize: FONT_SIZE_SMALL,
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    lineHeight: '13px',
    textAlign: 'center',
    textDecoration: 'none',
    textTransform: 'capitalize',
    flexBasis: '14.2857%',
    maxWidth: '14.2857%',
    overflow: 'hidden',
  },
});

const CalendarComponent = ({
  calendarController,
  dailyEventCount,
  onTodayClick,
  currentLanguage,
}: CalendarComponentProps) => {
  const { classes } = useStyles();

  return (
    <div data-testid="CalendarWrapper" className={classes.calendarContainer}>
      <Calendar
        calendarController={calendarController}
        classes={{ root: classes.calendarRoot }}
        data-testid="calendar"
        renderDay={({ day, onSelect }) => (
          <CalendarDay
            day={day}
            eventCount={dailyEventCount[day.toISOString()]}
            onClick={onSelect}
            selectedDay={calendarController.selectedDay}
          />
        )}
        renderNavigation={() => (
          <CalendarNavigation
            focusedDay={calendarController.focusedDay}
            onNext={calendarController.selectNext}
            onPrev={calendarController.selectPrevious}
            onTodayClick={onTodayClick}
            toggleCalendarFormat={calendarController.toggleCalendarFormat}
          />
        )}
        renderWeekDays={({ dayOfWeek }) => (
          <Typography className={classes.calendarWeekday} key={dayOfWeek}>
            {dayOfWeek.substring(0, currentLanguage === LanguagesEnum.DE ? 2 : 3)}
          </Typography>
        )}
        renderWeekends={({ dayOfWeek }) => (
          <Typography className={classes.calendarWeekday} key={dayOfWeek}>
            {dayOfWeek.substring(0, currentLanguage === LanguagesEnum.DE ? 2 : 3)}
          </Typography>
        )}
        weekDaysClasses={{ weekday: classes.calendarWeekday }}
      />
    </div>
  );
};

export default CalendarComponent;
